import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, Input,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, TextField, Typography, Autocomplete, Link, Stack,
  MenuItem, Select, Tabs, Tab, Table, TableBody, TableHead, TableRow, TableCell, IconButton, Tooltip
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import GetAppIcon from '@mui/icons-material/GetApp'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import stylesheet from '../detail-container.stylesheet'
import { ClassNameMap, withStyles } from '@mui/styles'
import DatePickerComponent from '../../../../utils/datepicker.component'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { checkCondition } from '../common/condition-check'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import ConfirmationDialogComponent from '../common/confirmation-dialog.component'
import Draggable from 'react-draggable'
import moment from 'moment'

interface IProps {
  openAddPatient: any
  onAddPatientClose: any
  classes: ClassNameMap
}

interface FhirImportEpicRawReport {
  name: string
  mrn: string
  import_type: string
  size: number
  created: number
}

const AddPatientComponent: React.FC<IProps> = ({
  openAddPatient,
  onAddPatientClose,
  classes,
}) => {
  const defaultValues = Helper.isAIDA()
    ? { mrn: '', program_name: ['amyloidosis_evaluation'] as string[], first_name: '', last_name: '', c_sex: '', birthdate: '' }
    : { mrn: '', program_name: ['cardiology_intake'] as string[], first_name: '', last_name: '', c_sex: '', birthdate: '' }
  const [selectedFile, setSelectedFile] = useState(null as any)
  const patientService = new PatientService()
  const [fileLoading, setFileLoading] = useState(false)
  const programsSet = useSelector((state: RootState) => (state.metaData.metadata?.PROGRAMS || []))
  const [rdValue, setRdValue] = React.useState(Helper.isAIDA() ? '' : 'ccd');
  const [formValues, setFormValues] = React.useState({ ...defaultValues });
  const addPatientRef = useRef(null) as any
  const mountedRef = useRef(true)
  // FHIR imports keep the dialog open on success so the raw JSON can be downloaded,
  // so the parent is told to refresh whenever the dialog is finally closed.
  const importSucceededRef = useRef(false)

  const [activeTab, setActiveTab] = useState(0)
  const [epicRawReports, setEpicRawReports] = useState<FhirImportEpicRawReport[]>([])
  const [loadingEpicRawReports, setLoadingEpicRawReports] = useState(false)
  const [pendingDeleteEpicRawReport, setPendingDeleteEpicRawReport] = useState<string | null>(null)

  useEffect(() => {
    return () => { mountedRef.current = false }
  }, [])

  const fetchEpicRawReports = useCallback(() => {
    setLoadingEpicRawReports(true)
    patientService.listFhirImportEpicRawReports().then((res: any) => {
      if (!mountedRef.current) return
      setEpicRawReports(res.data?.reports || [])
      setLoadingEpicRawReports(false)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      console.error('Failed to fetch FHIR import EPIC raw reports:', err)
      setLoadingEpicRawReports(false)
    })
  }, [])

  useEffect(() => {
    if (activeTab === 1) {
      fetchEpicRawReports()
    }
  }, [activeTab])

  const handleDialogClose = () => {
    onAddPatientClose(importSucceededRef.current)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleDownloadEpicRawReport = (filename: string) => {
    patientService.downloadFhirImportEpicRawReport(filename).then((res: any) => {
      if (!mountedRef.current) return
      Helper.handleTaskReportFile(res.data, filename)
    }).catch((err: any) => {
      toast.error('Failed to download report', Helper.bottom_center())
    })
  }

  const confirmDeleteEpicRawReportFile = (confirmed: boolean) => {
    if (confirmed && pendingDeleteEpicRawReport) {
      patientService.deleteFhirImportEpicRawReport(pendingDeleteEpicRawReport).then(() => {
        if (!mountedRef.current) return
        toast.success('Report deleted', Helper.bottom_center())
        setPendingDeleteEpicRawReport(null)
        fetchEpicRawReports()
      }).catch((err: any) => {
        toast.error('Failed to delete report', Helper.bottom_center())
        setPendingDeleteEpicRawReport(null)
      })
    } else {
      setPendingDeleteEpicRawReport(null)
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRdValue((event.target as HTMLInputElement).value);
    setFormValues({ ...defaultValues })
  }
  const handleCtrlChange = (event: any) => {
    if ((event.target as HTMLInputElement)?.name) {
      setFormValues({ ...formValues, [(event.target as HTMLInputElement).name]: (event.target as HTMLInputElement).value })
    }
  }

  const handleProgramChange = (prgData: any) => {
    if (Object.keys(prgData)?.length > 0) {
      setFormValues({ ...formValues, 'program_name': prgData?.map((i: any) => i.name) })
    }
  }

  const uploadFile = (e: any) => setSelectedFile(e.target.files[0])

  const showMsg = (id: any, type = 'added') => {
    const msg = <Typography variant="subtitle2">Patient{' '}
      <Link href={`patients/${id}/details`}><strong>{id}</strong></Link>{' '}{type} successfully.
    </Typography>
    toast.success(msg || `Patient ${type} successfully.`, Helper.bottom_center())
  }

  const handleSubmit = () => {
    setFileLoading(true)
    if (rdValue === 'ccd') {
      const formData = new FormData();
      formData.append("file", selectedFile);
      try {
        patientService
          .postNewPatient(formData)
          .then((res: any) => {
            setFileLoading(false)
            showMsg(res.data?.patient_id, 'added')
            onAddPatientClose(true)
          }).catch((err) => {
            toast.error(Helper.getErrorText(err), Helper.bottom_center())
            setFileLoading(false)
          })
      } catch (error) {
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
        setFileLoading(false)
      }
    } else if (rdValue === 'fhir') {
      try {
        patientService.fhirPatientAdd(formValues.mrn, formValues.program_name.join(',')).then((res) => {
          setFileLoading(false)
          showMsg(res?.data?.id, 'added')
          importSucceededRef.current = true
          setActiveTab(1)
        }).catch((err) => {
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
          setFileLoading(false)
        })
      }
      catch (error) {
        setFileLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      }
    } else if (rdValue === 'elation') {
      try {
        patientService.elationPatientAdd(formValues.mrn, formValues.program_name.join(','), 'atman').then((res) => {
          setFileLoading(false)
          showMsg(res.data?.id, 'added')
          onAddPatientClose(true)
        }).catch((err) => {
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
          setFileLoading(false)
        })
      }
      catch (error) {
        setFileLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      }
    } else if (rdValue === 'addPatient') {
      try {
        patientService.addManualPatient({ ...formValues, program_name: formValues.program_name.join() }).then((res) => {
          setFileLoading(false)
          showMsg(res.data?.patient?.ref_id || '', 'added')
          onAddPatientClose(true)
        }).catch((err) => {
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
          setFileLoading(false)
        })
      }
      catch (error) {
        setFileLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      }
    } else if (rdValue === 'json') {
      patientService.importPatient({
        "file": selectedFile,
        "is_transition_patient": true
      }).then((res: any) => {
        onAddPatientClose(true)
        showMsg(res?.data?.patient_id || '', 'imported')
      }).catch((err) => {
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setFileLoading(false)
      })
    }
  }

  const checkDisabledBtn = () => {
    if (rdValue === 'ccd' || rdValue === 'json') {
      return selectedFile === null
    } else if (['fhir', 'elation'].includes(rdValue)) {
      return !(formValues.program_name.length > 0 && formValues.mrn !== '')
    } else if (rdValue === 'addPatient') {
      // MRN is optional — backend assigns "TW-{ref_id}" when left blank
      return !(formValues.first_name.length && formValues.last_name.length && formValues.program_name.length)
    }
  }

  const sortedProgramsSet = [...programsSet]?.sort((a: any, b: any) => (a.name < b.name) ? -1 : 1)

  const recentProgramsSet = () => {
    return (Helper.isAIDA())
      ? [programsSet[programsSet?.findIndex((i: any) => i.name === 'amyloidosis_evaluation')] || '']
      : [programsSet[programsSet?.findIndex((i: any) => i.name === 'cardiology_intake')] || '']
  }

  return (
    <>
    <Draggable ref={addPatientRef} handle='.add-patient-dialog'>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openAddPatient}
        onClose={() => handleDialogClose()}
        aria-labelledby={'Add Import Patient'}
        title={'Add Import Patient'}
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          e.preventDefault();
          if (e.key === 'Enter' && !fileLoading && !checkDisabledBtn() && activeTab === 0)
            handleSubmit()
        }}
        sx={{
          position: "absolute",
          left: '15%',
          top: '15%'
        }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'add-patient-dialog' }}>
          <DialogTitleHeaderComponent title='Add / Import patient' handleDialogClose={() => handleDialogClose()} />
        </DialogTitle>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: '32px',
            px: 2,
            '& .MuiTab-root': {
              minHeight: '32px',
              py: 0.5,
              fontSize: '0.75rem',
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              height: '2px',
            },
          }}
        >
          <Tab label="Import" />
          <Tab label="EPIC Raw JSON" />
        </Tabs>
        <Grid item>{(fileLoading || loadingEpicRawReports) && <LinearProgress />}</Grid>
        <DialogContent className={classes.addPatientDialogContent}>
          {activeTab === 0 && (
          <Grid container spacing={1}>
            <Grid item md={12} xs={12}>
              <FormControl component="fieldset" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.65rem !important' } }}>
                {!Helper.isAIDA() &&
                  <FormLabel component="legend">
                    <Typography ml={'-0.2rem !important'} pb={1} variant='subtitle1' className={classes.desc}>Import type</Typography>
                  </FormLabel>
                }
                <RadioGroup row aria-label="import_type" name="import_type" value={rdValue} onChange={handleChange}>
                  {!Helper.isAIDA() && <FormControlLabel className={classes.desc} value="ccd" control={<Radio color="primary" size='small' />} label="CCD" />}
                  <FormControlLabel value="fhir" control={<Radio color="primary" size='small' />} label="FHIR" />
                  {!Helper.isAIDA() && <FormControlLabel value="elation" control={<Radio color="primary" size='small' />} label="ELATION" />}
                  <FormControlLabel value="addPatient" control={<Radio color="primary" size='small' />} label="Add patient" />
                  <FormControlLabel value="json" control={<Radio color="primary" size='small' />} label="JSON" />
                </RadioGroup>{ }
              </FormControl>
            </Grid>

            {checkCondition(rdValue === 'ccd' || rdValue === 'json', <>
              <Grid item>
                <FormControl>
                  <Input
                    inputProps={{ accept: checkCondition(rdValue === 'json', '.json', '.xlsx, .xls, .csv, .xml') }}
                    sx={{ position: 'absolute', display: 'none' }}
                    id="upload-file"
                    type="file"
                    onChange={uploadFile}
                  />
                  <label htmlFor="upload-file">
                    <Button component="span" variant="outlined" size="small">
                      <Typography variant='body2' className={classes.desc}>Choose file</Typography>
                    </Button>
                  </label>
                </FormControl>
              </Grid>
              <Grid item xs className={classes.selectedFile}>
                <Typography variant='body2' className={classes.desc} pt={1}>
                  {selectedFile !== undefined ? selectedFile?.name : ''}
                </Typography>
              </Grid>
            </>
            )}
            {['fhir', 'addPatient', 'elation'].includes(rdValue) && <>
              {rdValue === 'addPatient' && <>
                <Grid item md={12} xs={12} >
                  <FormControl className={classes.formControl}>
                    <FormLabel id="mrn-lable"><Typography variant='subtitle1' className={classes.desc}>First name</Typography></FormLabel>
                    <TextField variant="standard" id="mrn" name='first_name' value={formValues.first_name} className={classes.addPatientTextField}
                      onChange={handleCtrlChange} />
                  </FormControl>
                </Grid>
                <Grid item md={12} xs={12} >
                  <FormControl className={classes.formControl}>
                    <FormLabel id="mrn-lable"><Typography variant='subtitle1' className={classes.desc}>Last name</Typography></FormLabel>
                    <TextField variant="standard" id="mrn" name='last_name' value={formValues.last_name} className={classes.addPatientTextField}
                      onChange={handleCtrlChange} />
                  </FormControl>
                </Grid>
                <Grid item md={12} xs={12} >
                  <FormControl className={classes.formControl}>
                    <FormLabel><Typography variant='subtitle1' className={classes.desc}>Sex</Typography></FormLabel>
                    <Select variant="standard" displayEmpty name='c_sex' value={formValues.c_sex} className={classes.addPatientTextField}
                      onChange={handleCtrlChange}>
                      <MenuItem value="" className={classes.desc}>—</MenuItem>
                      <MenuItem value="Male" className={classes.desc}>Male</MenuItem>
                      <MenuItem value="Female" className={classes.desc}>Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item md={12} xs={12} >
                  <FormControl className={classes.formControl}>
                    <FormLabel><Typography variant='subtitle1' className={classes.desc}>Birthdate</Typography></FormLabel>
                    <DatePickerComponent name="birthdate" variant="standard" type="date"
                      cssClass={classes.addPatientTextField} value={formValues.birthdate}
                      onCalendarChange={handleCtrlChange} />
                  </FormControl>
                </Grid></>
              }
              <Grid item md={12} xs={12} >
                <FormControl className={classes.formControl}>
                  <FormLabel id="mrn-lable"><Typography variant='subtitle1' className={classes.desc}>MRN</Typography></FormLabel>
                  <TextField variant="standard" id="mrn" name='mrn' value={formValues.mrn} className={classes.addPatientTextField}
                    onChange={handleCtrlChange} />
                </FormControl>
              </Grid>
              <Grid item md={12} xs={12}>
                <FormControl className={classes.formControl}>
                  <FormLabel id="mutiple-checkbox-label">
                    <Typography variant='subtitle1' className={classes.desc}>Select programs</Typography>
                  </FormLabel>
                  <Autocomplete
                    multiple
                    id='program_name'
                    options={sortedProgramsSet}
                    getOptionLabel={(option: any) => option.display_name}
                    defaultValue={recentProgramsSet()}
                    readOnly={Helper.isAIDA() ? true : false}
                    disablePortal={false}
                    className={classes.chipSearch}
                    renderOption={(props, option, { selected }) => (
                      <Typography {...props} sx={{ padding: '0px !important', fontSize: '0.65rem !important' }} className={classes.desc}>
                        <Checkbox
                          sx={{ padding: '0.2rem 0.3rem !important', transform: 'scale(0.6)' }}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={selected}
                        />
                        {option.display_name}
                      </Typography>
                    )}
                    renderInput={(params: any) => (<TextField {...params} variant="standard" placeholder="" />)}
                    onChange={(event: any, newValue: any | null) => handleProgramChange(newValue)}
                    sx={{
                      minWidth: '320px',
                      '& .MuiChip-root': { height: '1rem !important', fontSize: '0.65rem !important' },
                      '&.MuiSvgIcon-root': { fontSize: '0.8rem !important', },
                    }}
                  />
                </FormControl>
              </Grid>
            </>}
          </Grid>
          )}

          {activeTab === 1 && (
            <>
              {epicRawReports.length === 0 && !loadingEpicRawReports ? (
                <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                  No EPIC raw JSON documents found. One document is generated per patient
                  imported via FHIR, containing every raw EPIC FHIR response fetched.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>MRN</strong></TableCell>
                      <TableCell><strong>File Name</strong></TableCell>
                      <TableCell><strong>Size</strong></TableCell>
                      <TableCell><strong>Created</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {epicRawReports.map((report) => (
                      <TableRow key={report.name} hover>
                        <TableCell>
                          <Typography variant="body2">{report.mrn || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{report.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatFileSize(report.size)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{moment.unix(report.created).format('MM/DD/YYYY hh:mm A')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`Download EPIC Raw JSON — ${report.mrn || report.name}`}>
                            <IconButton size="small" onClick={() => handleDownloadEpicRawReport(report.name)}>
                              <GetAppIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete document">
                            <IconButton size="small" onClick={() => setPendingDeleteEpicRawReport(report.name)}>
                              <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        {activeTab === 0 && (
        <DialogActions>
          <Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                disabled={fileLoading || checkDisabledBtn()}
                onClick={() => handleSubmit()}
                component="label"
                className={fileLoading || checkDisabledBtn() ? classes.disabledBtn : ''}
              >
                Submit
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                disabled={fileLoading}
                onClick={() => handleDialogClose()}
                className={fileLoading ? classes.disabledBtn : ''}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
        )}
      </Dialog>
    </Draggable>
    {/* Outside <Draggable>: react-draggable requires exactly one child and
        dereferences children.props before validating it. */}
    {pendingDeleteEpicRawReport && (
      <ConfirmationDialogComponent
        open={!!pendingDeleteEpicRawReport}
        content={`Are you sure you want to delete document "${pendingDeleteEpicRawReport}"?`}
        handleConfirmClose={() => confirmDeleteEpicRawReportFile(false)}
        confirmStatus={() => confirmDeleteEpicRawReportFile(true)}
      />
    )}
    </>
  )
}

export default withStyles(stylesheet)(AddPatientComponent)
